import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { authLogin, authMe, authLogout, getMyInterventions, endIntervention, checkinIntervention, checkoutIntervention, uploadFile, fileUrl } from "../api";
import { useToast } from "../hooks/use-toast";
import MyDay from "../components/collab/MyDay";
import AbsenceRequest from "../components/collab/AbsenceRequest";
import SignatureCanvas from "../components/collab/SignaturePad";
import QualityTab from "../components/collab/QualityTab";
import {
  MapPin, Sun, PlayCircle, StopCircle, Camera, Loader2, CheckCircle2, LogOut, Navigation, X, PenLine,
  ShieldCheck, LayoutDashboard, CalendarDays, ClipboardCheck, Clock, AlertTriangle, ExternalLink, Users, ListChecks, Plus,
} from "lucide-react";
import { log } from "../lib/log";
import { joinAddress } from "../lib/address";


/* Pointage : arrivée / départ / clôture — libellés, couleur et icône par mode. */
const pointMode = (isEnd, isCheckout) => {
  if (isEnd) return "end";
  return isCheckout ? "checkout" : "checkin";
};
const MODE_UI = {
  checkin: { title: "Pointer mon arrivée", submitLabel: "Confirmer mon arrivée", accent: "bg-brand hover:bg-brand-dark", Icon: PlayCircle },
  checkout: { title: "Pointer mon départ", submitLabel: "Confirmer mon départ", accent: "bg-neutral-800 hover:bg-neutral-900", Icon: Clock },
  end: { title: "Clôture & rapport de prestation", submitLabel: "Valider le rapport & clôturer", accent: "bg-emerald-600 hover:bg-emerald-700", Icon: StopCircle },
};
const gpsStatus = (locating, gps) => {
  if (locating) return "Localisation…";
  return gps ? "Position GPS confirmée ✓" : "Position indisponible";
};
const STATUS_UI = {
  en_cours: { cls: "bg-amber-100 text-amber-700", label: "En cours" },
  terminee: { cls: "bg-emerald-100 text-emerald-700", label: "Terminée" },
  default: { cls: "bg-neutral-200 text-neutral-700", label: "À venir" },
};
const fmtDT = (x) => (x ? new Date(x).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");const fmtTime = (x) => (x ? new Date(x).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—");
const duration = (s, e) => {
  if (!s || !e) return "";
  const m = Math.max(0, Math.round((new Date(e) - new Date(s)) / 60000));
  const h = Math.floor(m / 60);
  return h ? `${h}h${String(m % 60).padStart(2, "0")}` : `${m} min`;
};

const STANDARD_TASKS = ["Vitres", "Sols", "Sanitaires", "Dépoussiérage/surfaces", "Poubelles", "Cuisine/coin repas"];

/* ---------- Login ---------- */
const Login = ({ onLogin }) => {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await authLogin(login, password);
      sessionStorage.setItem("crm_login", res.data.login || login);
      onLogin();
    } catch (err) {
      toast({ title: "Accès refusé", description: err?.response?.data?.detail || "Identifiant ou clé incorrecte", variant: "destructive" });
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-neutral-950 flex items-center justify-center px-5">
      <form onSubmit={submit} className="bg-neutral-900 border border-neutral-800 rounded-2xl p-7 w-full max-w-sm" data-testid="collab-login-form">
        <div className="flex items-center gap-3 mb-1">
          <div className="bg-brand text-white rounded-lg p-2.5"><Navigation size={20} /></div>
          <div>
            <h1 className="font-heading font-bold text-white text-lg leading-none">App Intervenant</h1>
            <p className="text-xs text-neutral-400 mt-1">PTITSERVICES-PRO</p>
          </div>
        </div>
        <p className="text-sm text-neutral-400 my-4">Connectez-vous pour gérer et pointer vos prestations.</p>
        <input type="text" value={login} onChange={(e) => setLogin(e.target.value)} placeholder="Identifiant" autoFocus
          className="w-full bg-neutral-800 border border-neutral-700 rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:border-brand mb-3" data-testid="collab-login-input" />
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Clé API Dolibarr"
          className="w-full bg-neutral-800 border border-neutral-700 rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:border-brand" data-testid="collab-password-input" />
        <button type="submit" disabled={loading} className="mt-5 w-full bg-brand hover:bg-brand-dark text-white font-semibold py-3 rounded-md disabled:opacity-60" data-testid="collab-login-submit">
          {loading ? "Connexion..." : "Se connecter"}
        </button>
      </form>
    </div>
  );
};


/* ---------- Pointage modal (arrivée / départ / rapport de clôture) ---------- */
const PointageModal = ({ intv, mode, onClose, onDone }) => {
  const isEnd = mode === "end";
  const isCheckout = mode === "checkout";
  const isCheckin = mode === "checkin";
  const [gps, setGps] = useState(null);
  const [gpsErr, setGpsErr] = useState(null);
  const [locating, setLocating] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState("");
  const [captions, setCaptions] = useState({});
  const [tasks, setTasks] = useState([]);
  const customTasks = useMemo(() => tasks.filter((t) => !STANDARD_TASKS.includes(t)), [tasks]);
  const [customTask, setCustomTask] = useState("");
  const [hasSig, setHasSig] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const sigRef = useRef(null);
  const { toast } = useToast();

  const title = MODE_UI[pointMode(isEnd, isCheckout)].title;

  const locate = () => {
    setLocating(true); setGpsErr(null);
    if (!navigator.geolocation) { setGpsErr("Géolocalisation non supportée"); setLocating(false); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => { setGps({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }); setLocating(false); },
      (e) => { setGpsErr(e.message || "Position indisponible"); setLocating(false); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };
   
  useEffect(() => { locate(); }, []);

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) { const res = await uploadFile(f); paths.push(res.data.path); }
      setPhotos((p) => [...p, ...paths]);
    } catch { toast({ title: "Échec de l'envoi de la photo", variant: "destructive" }); }
    finally { setUploading(false); }
  };

  const uploadSignature = async () => {
    const canvas = sigRef.current;
    if (!canvas) return "";
    return new Promise((resolve) => {
      canvas.toBlob(async (blob) => {
        if (!blob) return resolve("");
        try { const res = await uploadFile(new File([blob], "signature.png", { type: "image/png" })); resolve(res.data.path); }
        catch { resolve(""); }
      }, "image/png");
    });
  };

  const reportOk = !isEnd || (photos.length > 0 && hasSig);

  const submit = async () => {
    if (!gps) { toast({ title: "Position GPS requise", variant: "destructive" }); return; }
    if (isEnd && !reportOk) { toast({ title: "Rapport incomplet", description: "Ajoutez au moins une photo et la signature du client.", variant: "destructive" }); return; }
    setSubmitting(true);
    try {
      if (isEnd) {
        const signature = await uploadSignature();
        await endIntervention(intv.id, { gps, photos, note, signature, captions, tasks, origin: window.location.origin });
        toast({ title: "Prestation clôturée & rapport validé ✓" });
      } else if (isCheckout) {
        await checkoutIntervention(intv.id, { gps });
        toast({ title: "Départ pointé ✓" });
      } else {
        await checkinIntervention(intv.id, { gps, photos, note, captions });
        toast({ title: "Arrivée pointée ✓" });
      }
      onDone();
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Échec du pointage", variant: "destructive" });
    } finally { setSubmitting(false); }
  };

  const { submitLabel, accent, Icon: ModeIcon } = MODE_UI[pointMode(isEnd, isCheckout)];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" data-testid="pointage-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-neutral-100 px-5 py-4 flex items-center justify-between">
          <h3 className="font-heading font-bold text-neutral-900">{title}</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700" data-testid="pointage-close"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-neutral-50 rounded-lg p-3">
            <p className="font-semibold text-neutral-900">{intv.client_nom}</p>
            <p className="text-sm text-neutral-500">{joinAddress(intv.adresse, intv.ville)}</p>
          </div>

          {isEnd && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              Pour clôturer, vous devez valider le rapport : au moins une photo « après » et la signature du client.
            </div>
          )}

          <div className={`rounded-lg p-3 border ${gps ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="gps-status">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <MapPin size={16} className={gps ? "text-emerald-600" : "text-amber-600"} />
              {gpsStatus(locating, gps)}
            </div>
            {gps && <p className="text-xs text-neutral-500 mt-1">{gps.lat.toFixed(5)}, {gps.lng.toFixed(5)} (± {Math.round(gps.accuracy)} m)</p>}
            {gpsErr && <p className="text-xs text-amber-700 mt-1">{gpsErr}</p>}
            {(!gps && !locating) && <button onClick={locate} className="text-xs text-brand font-semibold mt-1">Réessayer</button>}
          </div>

          {(isCheckin || isEnd) && (
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1.5">
                Photos {isEnd ? "après" : "avant"} {isEnd && <span className="text-red-500">*</span>}
              </label>
              <label className="flex items-center justify-center gap-2 border-2 border-dashed border-neutral-300 rounded-lg py-3 text-sm text-neutral-500 cursor-pointer hover:border-brand">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />} Ajouter des photos
                <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={addPhotos} data-testid="photo-input" />
              </label>
              {photos.length > 0 && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  {photos.map((p, i) => (
                    <div key={p}>
                      <img src={fileUrl(p)} alt="" className="w-full h-24 object-cover rounded-md border border-neutral-200" />
                      <input value={captions[p] || ""} onChange={(e) => setCaptions((c) => ({ ...c, [p]: e.target.value }))}
                        placeholder="Légende (optionnel)" data-testid={`caption-input-${i}`}
                        className="mt-1 w-full border border-neutral-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-brand" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {isEnd && (
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1.5 flex items-center gap-1"><ListChecks size={14} /> Tâches effectuées</label>
              {intv.tasks_planned?.length > 0 && <p className="text-xs text-brand mb-1.5">Prévu au contrat : {intv.tasks_planned.join(", ")}</p>}
              <div className="grid grid-cols-2 gap-2">
                {[...new Set([...(intv.tasks_planned || []), ...STANDARD_TASKS])].map((t) => {
                  const on = tasks.includes(t);
                  return (
                    <button type="button" key={t} data-testid={`task-${t}`}
                      onClick={() => setTasks((prev) => on ? prev.filter((x) => x !== t) : [...prev, t])}
                      className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 border text-left transition-colors ${on ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-neutral-300 text-neutral-600 hover:border-neutral-400"}`}>
                      <span className={`w-4 h-4 shrink-0 rounded flex items-center justify-center text-[10px] ${on ? "bg-emerald-600 text-white" : "border border-neutral-300"}`}>{on ? "✓" : ""}</span>
                      {t}
                    </button>
                  );
                })}
              </div>
              {customTasks.map((t) => (
                <div key={t} className="flex items-center gap-2 mt-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-500 rounded-lg px-3 py-2">
                  <span className="w-4 h-4 shrink-0 rounded bg-emerald-600 text-white flex items-center justify-center text-[10px]">✓</span>
                  <span className="flex-1">{t}</span>
                  <button type="button" onClick={() => setTasks((prev) => prev.filter((x) => x !== t))} className="text-neutral-400 hover:text-red-500"><X size={14} /></button>
                </div>
              ))}
              <div className="flex gap-2 mt-2">
                <input value={customTask} onChange={(e) => setCustomTask(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); const v = customTask.trim(); if (v && !tasks.includes(v)) setTasks((p) => [...p, v]); setCustomTask(""); } }}
                  placeholder="Ajouter une tâche…" data-testid="custom-task-input"
                  className="flex-1 border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand" />
                <button type="button" data-testid="add-task-btn"
                  onClick={() => { const v = customTask.trim(); if (v && !tasks.includes(v)) setTasks((p) => [...p, v]); setCustomTask(""); }}
                  className="flex items-center gap-1 bg-neutral-800 hover:bg-neutral-900 text-white text-sm font-semibold px-3 rounded-lg"><Plus size={16} /></button>
              </div>
            </div>
          )}

          {isEnd && (
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1.5 flex items-center gap-1"><PenLine size={14} /> Signature du client <span className="text-red-500">*</span></label>
              <SignatureCanvas ref={sigRef} onDraw={setHasSig} />
            </div>
          )}

          {(isCheckin || isEnd) && (
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1.5">Commentaire</label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optionnel"
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand" data-testid="note-input" />
            </div>
          )}

          {isEnd && (
            <div className="text-xs space-y-1" data-testid="report-checklist">
              <p className={photos.length > 0 ? "text-emerald-600" : "text-neutral-400"}>{photos.length > 0 ? "✓" : "○"} Photo(s) après ajoutée(s)</p>
              <p className={hasSig ? "text-emerald-600" : "text-neutral-400"}>{hasSig ? "✓" : "○"} Signature du client</p>
            </div>
          )}

          <button onClick={submit} disabled={submitting || uploading || !gps || (isEnd && !reportOk)} data-testid="pointage-submit"
            className={`w-full flex items-center justify-center gap-2 text-white font-semibold py-3.5 rounded-xl disabled:opacity-50 ${accent}`}>
            {submitting ? <Loader2 size={18} className="animate-spin" /> : <ModeIcon size={18} />}
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------- Reusable intervention row ---------- */
const IntvRow = ({ it, children }) => (
  <div className="bg-white border border-neutral-200 rounded-2xl p-4 shadow-sm" data-testid={`collab-card-${it.id}`}>
    <div className="flex items-center gap-2 mb-1">
      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${(STATUS_UI[it.status] || STATUS_UI.default).cls}`}>
        {(STATUS_UI[it.status] || STATUS_UI.default).label}
      </span>
      <span className="text-xs text-neutral-400">{fmtDT(it.date)}</span>
    </div>
    <p className="font-heading font-bold text-neutral-900">{it.client_nom}</p>
    <p className="text-sm text-neutral-500">{it.prestation || "Nettoyage"}</p>
    <p className="text-sm text-neutral-500 flex items-center gap-1 mt-0.5"><MapPin size={13} /> {[it.adresse, it.ville].filter(Boolean).join(", ") || "—"}</p>
    {it.collaborateurs?.length > 1 && <p className="text-xs text-neutral-400 mt-1 flex items-center gap-1"><Users size={12} /> {it.collaborateurs.map((c) => c.nom).join(", ")}</p>}
    {children}
  </div>
);

/* ---------- Main ---------- */
const CollaboratorApp = () => {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("jour");
  const [myRole, setMyRole] = useState("");
  const [modal, setModal] = useState(null);
  const [myLogin, setMyLogin] = useState("");
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const res = await getMyInterventions();
      setItems(res.data);
    } catch (error) {
      log.error("Chargement des interventions impossible :", error);
      toast({ title: "Chargement impossible", description: "Vérifiez votre connexion puis réessayez.", variant: "destructive" });
    }
  }, [toast]);
  useEffect(() => {
    authMe().then((res) => { setAuthed(true); setMyLogin(res.data.login || ""); setMyRole(res.data.role || ""); load(); }).catch(() => setAuthed(false)).finally(() => setChecking(false));
  }, [load]);
  const upcoming = useMemo(() => items.filter((i) => i.status === "planifiee").sort((a, b) => new Date(a.date) - new Date(b.date)), [items]);
  const active = useMemo(() => items.filter((i) => i.status === "en_cours"), [items]);
  const done = useMemo(() => items.filter((i) => i.status === "terminee"), [items]);
  const sortedItems = useMemo(() => [...items].sort((a, b) => new Date(a.date) - new Date(b.date)), [items]);

  if (checking) return <div className="min-h-screen bg-neutral-950 flex items-center justify-center"><span className="text-neutral-400 text-sm">Chargement…</span></div>;
  if (!authed) return <Login onLogin={() => { authMe().then((r) => { setMyLogin(r.data.login || ""); setMyRole(r.data.role || ""); }).catch((e) => log.error("authMe:", e)); setAuthed(true); load(); }} />;
  const isMajor = myRole === "major" || myRole === "master";

  const user = sessionStorage.getItem("crm_login") || "intervenant";
  const logout = async () => {
    try { await authLogout(); } catch (error) { log.error("Déconnexion :", error); }
    setAuthed(false);
  };

  const myOpen = (it) => (it.pointages || []).some((p) => p.login === myLogin && !p.end_at);
  const myPointage = (it) => (it.pointages || []).filter((p) => p.login === myLogin).slice(-1)[0];

  const weekMinutes = () => {
    const now = new Date();
    const day = (now.getDay() + 6) % 7; // Monday = 0
    const monday = new Date(now); monday.setHours(0, 0, 0, 0); monday.setDate(now.getDate() - day);
    let mins = 0;
    items.forEach((it) => (it.pointages || []).forEach((p) => {
      if (p.login === myLogin && p.start_at && p.end_at) {
        const s = new Date(p.start_at);
        if (s >= monday) mins += Math.max(0, (new Date(p.end_at) - s) / 60000);
      }
    }));
    return Math.round(mins);
  };
  const weekLabel = () => {
    const m = weekMinutes();
    const h = Math.floor(m / 60);
    return h ? `${h}h${String(m % 60).padStart(2, "0")}` : `${m} min`;
  };

  const Stat = ({ icon: Icon, label, value, color }) => (
    <div className="bg-white border border-neutral-200 rounded-2xl p-4 text-center">
      <Icon size={20} className={`mx-auto ${color}`} />
      <p className="font-heading font-extrabold text-2xl text-neutral-900 mt-1.5">{value}</p>
      <p className="text-xs text-neutral-500">{label}</p>
    </div>
  );

  const Actions = ({ it }) => {
    if (it.status === "terminee") return null;
    if (myOpen(it)) {
      return (
        <div className="mt-4 space-y-2">
          <button onClick={() => setModal({ intv: it, mode: "end" })} data-testid={`end-btn-${it.id}`}
            className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-xl">
            <StopCircle size={18} /> Clôturer & valider le rapport
          </button>
          <button onClick={() => setModal({ intv: it, mode: "checkout" })} data-testid={`checkout-btn-${it.id}`}
            className="w-full flex items-center justify-center gap-2 border border-neutral-300 text-neutral-700 hover:border-neutral-800 font-semibold py-2.5 rounded-xl">
            <Clock size={16} /> Pointer mon départ
          </button>
        </div>
      );
    }
    return (
      <button onClick={() => setModal({ intv: it, mode: "checkin" })} data-testid={`start-btn-${it.id}`}
        className="mt-4 w-full flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark text-white font-semibold py-3 rounded-xl">
        <PlayCircle size={18} /> Pointer mon arrivée
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-neutral-100 font-body pb-24">
      <header className="bg-neutral-950 text-white sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-brand rounded-lg p-1.5"><Navigation size={16} /></div>
            <div>
              <p className="font-heading font-bold text-sm leading-none">App Intervenant</p>
              <p className="text-xs text-neutral-400 mt-0.5">{user}</p>
            </div>
          </div>
          <button onClick={logout} className="text-neutral-300 hover:text-white" data-testid="collab-logout"><LogOut size={18} /></button>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-5 py-6 space-y-6">
        {/* DASHBOARD */}
        {tab === "dash" && (
          <div className="space-y-6" data-testid="collab-dashboard">
            <div className="grid grid-cols-3 gap-3">
              <Stat icon={CalendarDays} label="À venir" value={upcoming.length} color="text-brand" />
              <Stat icon={PlayCircle} label="En cours" value={active.length} color="text-amber-500" />
              <Stat icon={CheckCircle2} label="Terminées" value={done.length} color="text-emerald-500" />
            </div>

            <div className="bg-brand text-white rounded-2xl p-4 flex items-center justify-between" data-testid="week-hours">
              <div className="flex items-center gap-2">
                <Clock size={20} />
                <span className="text-sm font-medium">Heures travaillées cette semaine</span>
              </div>
              <span className="font-heading font-extrabold text-2xl">{weekLabel()}</span>
            </div>

            {active.length > 0 && (
              <div>
                <h2 className="font-heading font-bold text-neutral-900 mb-2 flex items-center gap-2"><PlayCircle size={18} className="text-amber-500" /> Prestation en cours</h2>
                <div className="space-y-3">
                  {active.map((it) => (
                    <IntvRow key={it.id} it={it}>
                      <p className="text-xs text-neutral-500 mt-2 flex items-center gap-1"><Clock size={12} /> Démarrée à {fmtTime(it.start_at)}</p>
                      <Actions it={it} />
                    </IntvRow>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h2 className="font-heading font-bold text-neutral-900 mb-2 flex items-center gap-2"><CalendarDays size={18} className="text-brand" /> Prochaines interventions</h2>
              {upcoming.length === 0 ? (
                <p className="text-sm text-neutral-500 bg-white rounded-xl p-5 text-center">Aucune prestation à venir.</p>
              ) : (
                <div className="space-y-3">{upcoming.map((it) => <IntvRow key={it.id} it={it}><Actions it={it} /></IntvRow>)}</div>
              )}
            </div>
            <AbsenceRequest />
          </div>
        )}

        {/* MA JOURNÉE */}
        {tab === "jour" && <MyDay items={items} renderActions={(it) => <Actions it={it} />} />}

        {/* AGENDA */}
        {tab === "agenda" && (
          <div data-testid="collab-agenda">
            <h2 className="font-heading font-bold text-neutral-900 mb-3">Agenda</h2>
            {items.length === 0 ? (
              <p className="text-sm text-neutral-500 bg-white rounded-xl p-5 text-center">Aucune intervention.</p>
            ) : (
              <div className="space-y-3">
                {sortedItems.map((it) => (
                  <IntvRow key={it.id} it={it}>
                    {it.status !== "terminee" && <Actions it={it} />}
                  </IntvRow>
                ))}
              </div>
            )}
          </div>
        )}

        {/* SUIVI */}
        {tab === "qualite" && isMajor && <QualityTab login={myLogin} />}
        {tab === "suivi" && (
          <div data-testid="collab-suivi">
            <h2 className="font-heading font-bold text-neutral-900 mb-3">Suivi des prestations réalisées</h2>
            {done.length === 0 ? (
              <p className="text-sm text-neutral-500 bg-white rounded-xl p-5 text-center">Aucune prestation terminée.</p>
            ) : (
              <div className="space-y-3">
                {done.map((it) => (
                  <IntvRow key={it.id} it={it}>
                    <div className="mt-2 text-sm text-neutral-600 flex flex-wrap gap-x-4 gap-y-1">
                      <span className="flex items-center gap-1"><Clock size={13} /> {fmtTime(it.start_at)} → {fmtTime(it.end_at)}</span>
                      {duration(it.start_at, it.end_at) && <span>Durée : {duration(it.start_at, it.end_at)}</span>}
                    </div>
                    {myPointage(it) && (
                      <p className="mt-1 text-xs text-brand">Vos heures : {fmtTime(myPointage(it).start_at)} → {fmtTime(myPointage(it).end_at)} ({duration(myPointage(it).start_at, myPointage(it).end_at) || "—"})</p>
                    )}
                    {it.notify_client && (
                      <a href={`/suivi/${it.share_token}`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm text-brand hover:underline">
                        <ExternalLink size={14} /> Voir la page de suivi client
                      </a>
                    )}
                  </IntvRow>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 bg-white border-t border-neutral-200 z-40">
        <div className={`max-w-lg mx-auto grid ${isMajor ? "grid-cols-5" : "grid-cols-4"}`}>
          {[
            { id: "jour", label: "Ma journée", icon: Sun },
            { id: "dash", label: "Tableau de bord", icon: LayoutDashboard },
            { id: "agenda", label: "Agenda", icon: CalendarDays },
            { id: "suivi", label: "Suivi", icon: ClipboardCheck },
            ...(isMajor ? [{ id: "qualite", label: "Qualité", icon: ShieldCheck }] : []),
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`nav-${t.id}`}
              className={`flex flex-col items-center gap-0.5 py-2.5 text-xs font-medium ${tab === t.id ? "text-brand" : "text-neutral-500"}`}>
              <t.icon size={20} /> {t.label}
            </button>
          ))}
        </div>
      </nav>

      {modal && (
        <PointageModal intv={modal.intv} mode={modal.mode} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />
      )}
    </div>
  );
};

export default CollaboratorApp;
